import { FontAwesome } from '@expo/vector-icons';
import {
  secondaryFontFamily,
  styled,
  useDimension,
} from '@openeducation/oe-it-themes';
import React, { useMemo } from 'react';
import {
  StyleProp,
  TouchableOpacity,
  useWindowDimensions,
  View,
  ViewStyle,
} from 'react-native';
import { useTheme } from 'react-native-paper';

import { OEButton, OEText } from '../../atoms';
import { color, flex, mt } from '../../style-utils';

export interface ToastDefaultProps {
  message: string;
  type: 'success' | 'warning' | 'error';
  onClose: () => void;
  buttonLabel?: string;
  onPressButton?: () => void;
  containerStyle?: StyleProp<ViewStyle>;
  textContainerStyle?: StyleProp<ViewStyle>;
}

type ColorSchema = {
  text: string;
  background: string;
  border: string;
};

export const ToastDefault: React.FC<ToastDefaultProps> = ({
  message,
  type,
  onClose,
  buttonLabel,
  onPressButton,
  containerStyle,
  textContainerStyle,
}) => {
  const { width } = useWindowDimensions();
  const { colors } = useTheme();
  const { isMobile } = useDimension();

  const hitSlop = { left: 5, right: 5, top: 5, bottom: 5 };

  const colorSchema: ColorSchema = useMemo(() => {
    switch (type) {
      case 'warning':
        return {
          text: colors.toastWarningText,
          background: colors.toastWarningBackground,
          border: colors.toastWarningBorder,
        };
      case 'error':
        return {
          text: colors.background,
          background: colors.error,
          border: colors.error,
        };
      case 'success':
      default:
        return {
          text: colors.toastSuccessText,
          background: colors.toastSuccessBackground,
          border: colors.toastSuccessBorder,
        };
    }
  }, [
    colors.background,
    colors.error,
    colors.toastSuccessBackground,
    colors.toastSuccessBorder,
    colors.toastSuccessText,
    colors.toastWarningBackground,
    colors.toastWarningBorder,
    colors.toastWarningText,
    type,
  ]);

  return (
    <Container
      isMobile={isMobile}
      width={width}
      colorSchema={colorSchema}
      style={containerStyle}
    >
      <HeaderContainer>
        <TextContainer style={textContainerStyle}>
          <Message style={[color(colorSchema.text)]}>{message}</Message>
        </TextContainer>
        <TouchableOpacity onPress={onClose} hitSlop={hitSlop}>
          <FontAwesome size={16} name="close" color={colorSchema.text} />
        </TouchableOpacity>
      </HeaderContainer>

      {onPressButton && buttonLabel && (
        <OEButton
          color={colors.background}
          onPress={() => {
            onPressButton();
            onClose();
          }}
          style={[flex('align-self-start'), mt(16)]}
        >
          <OEText
            color={colorSchema.background}
            fontType="bold"
            fontFamily={secondaryFontFamily}
            fontSize={16}
          >
            {buttonLabel}
          </OEText>
        </OEButton>
      )}
    </Container>
  );
};

const Container = styled(View)<{
  colorSchema: ColorSchema;
  isMobile: boolean;
  width: number;
}>`
  align-self: center;
  margin: 0 24px;
  border-radius: 4px;
  border-width: 1px;
  padding: 12px 16px;
  background-color: ${({ colorSchema }) => colorSchema.background};
  border-color: ${({ colorSchema }) => colorSchema.border};
  width: auto;
`;

const HeaderContainer = styled.View`
  flex-direction: row;
`;

const TextContainer = styled.View`
  padding-right: 16px;
`;

const Message = styled.Text`
  font-size: 16px;
  font-weight: 400;
`;
